// CSV, read by hand (RFC 4180), with no dependency and nothing executed (ADR-0048).
//
// Reading: an incremental state machine. The server feeds a large file in chunks and yields between them, so a 20 MB
// export never holds the event loop for long, and the same reader runs in a console preview. It handles a byte-order
// mark, CRLF / LF / CR line ends, quoted fields with commas, line breaks and doubled quotes inside, and a missing final
// newline. A row longer than `maxFieldsPerRow` or a field longer than `maxFieldLength` stops the read: an export with a
// 10 MB cell is not an export.
//
// The delimiter is guessed from the first line (comma, semicolon or tab — a European Excel writes semicolons) unless
// it is given.
//
// Writing a CSV (the import report) is the server's `http/csv.ts`, which neutralises formulas.
export const DEFAULT_CSV_LIMITS = { maxFieldsPerRow: 200, maxFieldLength: 10_000, maxRows: 200_000 };
export class CsvReadError extends Error {
    name = 'CsvReadError';
    failure;
    row;
    constructor(failure, row) {
        super(`The CSV could not be read: ${failure} at row ${row}.`);
        this.failure = failure;
        this.row = row;
    }
}
/** Guess the delimiter from the first line: the candidate that occurs most outside quotes; a comma on a tie. */
export function guessCsvDelimiter(firstChunk) {
    const line = firstChunk.replace(/^\ufeff/, '').split(/\r\n|\n|\r/, 1)[0] ?? '';
    const counts = { ',': 0, ';': 0, '\t': 0 };
    let quoted = false;
    for (const char of line) {
        if (char === '"')
            quoted = !quoted;
        else if (!quoted && (char === ',' || char === ';' || char === '\t'))
            counts[char] += 1;
    }
    if (counts[';'] > counts[','] && counts[';'] >= counts['\t'])
        return ';';
    if (counts['\t'] > counts[','] && counts['\t'] > counts[';'])
        return '\t';
    return ',';
}
/** An incremental CSV reader: `push` chunks, then `end`; complete rows are returned as they finish. */
export class CsvReader {
    delimiter;
    limits;
    field = '';
    row = [];
    quoted = false;
    quoteSeen = false;
    pendingCr = false;
    first = true;
    rows = 0;
    constructor(options = {}) {
        this.delimiter = options.delimiter;
        this.limits = { ...DEFAULT_CSV_LIMITS, ...options.limits };
    }
    /** Feed the next chunk of text; returns the rows it completed. */
    push(chunk) {
        let text = chunk;
        if (this.first) {
            text = text.replace(/^\ufeff/, '');
            this.delimiter ??= guessCsvDelimiter(text);
            this.first = false;
        }
        const out = [];
        const delimiter = this.delimiter ?? ',';
        for (let index = 0; index < text.length; index += 1) {
            const char = text[index];
            if (this.pendingCr) {
                this.pendingCr = false;
                if (char === '\n')
                    continue;
            }
            if (this.quoted) {
                if (char === '"') {
                    this.quoted = false;
                    this.quoteSeen = true;
                }
                else {
                    this.append(char);
                }
                continue;
            }
            if (char === '"' && (this.quoteSeen || this.field === '')) {
                // A doubled quote inside a quoted field ("a ""b"" c"), or the opening quote of a field. A quote in the middle
                // of an unquoted field is kept as a character, as spreadsheets write it.
                if (this.quoteSeen)
                    this.append('"');
                this.quoted = true;
                this.quoteSeen = false;
                continue;
            }
            this.quoteSeen = false;
            if (char === delimiter) {
                this.endField();
            }
            else if (char === '\n' || char === '\r') {
                if (char === '\r')
                    this.pendingCr = true;
                this.endRow(out);
            }
            else {
                this.append(char);
            }
        }
        return out;
    }
    /** The input is over: the last row, if the file had no final newline. */
    end() {
        if (this.quoted)
            throw new CsvReadError('unclosed-quote', this.rows + 1);
        const out = [];
        if (this.field !== '' || this.row.length > 0)
            this.endRow(out);
        return out;
    }
    append(char) {
        this.field += char;
        if (this.field.length > this.limits.maxFieldLength)
            throw new CsvReadError('field-too-long', this.rows + 1);
    }
    endField() {
        this.row.push(this.field);
        this.field = '';
        if (this.row.length > this.limits.maxFieldsPerRow)
            throw new CsvReadError('too-many-fields', this.rows + 1);
    }
    endRow(out) {
        this.endField();
        const row = this.row;
        this.row = [];
        // A blank line is no row.
        if (row.length === 1 && row[0] === '')
            return;
        this.rows += 1;
        if (this.rows > this.limits.maxRows)
            throw new CsvReadError('too-many-rows', this.rows);
        out.push(row);
    }
}
/** Read a whole CSV text at once (tests, small files). */
export function readCsv(text, options = {}) {
    const reader = new CsvReader(options);
    return [...reader.push(text), ...reader.end()];
}
//# sourceMappingURL=csv.js.map